import { useState, forwardRef } from "react";
import {
  MoreHorizontal,
  Edit,
  Trash2,
  User,
  MapPin,
  Package,
  Calendar,
  ScrollText,
} from "lucide-react";
import { useSortable, type UseSortableArguments } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import type { DraggableAttributes } from "@dnd-kit/core";
import type { SyntheticListenerMap } from "@dnd-kit/core/dist/hooks/utilities";
import { Button } from "@/client/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/client/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/client/components/ui/tooltip";
import { CodexTooltipContent } from "@/client/components/codex/codex-tooltip";
import { getCodexImageUrl, usePortraitChanges } from "@/client/components/codex/codex-image-picker";
import { cn } from "@/client/lib/utils";
import type { CodexEntry as CodexEntryType } from "@/client/db";

const TYPE_ICONS = {
  character: User,
  location: MapPin,
  object: Package,
  event: Calendar,
  lore: ScrollText,
} as const;

interface CodexEntryProps {
  entry: CodexEntryType;
  onOpen: () => void;
  onDelete: () => void;
  isActive?: boolean;
  isDragging?: boolean;
  style?: React.CSSProperties;
  attributes?: DraggableAttributes;
  listeners?: SyntheticListenerMap;
}

// Base entry component (used for both regular and drag overlay)
export const CodexEntry = forwardRef<HTMLDivElement, CodexEntryProps>(
  function CodexEntry({ entry, onOpen, onDelete, isActive, isDragging, style, attributes, listeners }, ref) {
    const [dropdownOpen, setDropdownOpen] = useState(false);
    usePortraitChanges();
    const Icon = TYPE_ICONS[entry.type] || User;

    const hasDescription = !!entry.content;

    return (
      // biome-ignore lint/a11y/useSemanticElements: button styling doesn't fit here
      <div 
        ref={ref}
        role="button"
        tabIndex={0}
        style={style}
        data-region="row"
        data-active={isActive || undefined}
        className={cn(
          "group grid grid-cols-[32px_1fr_24px] gap-2 py-2 px-2 rounded cursor-grab active:cursor-grabbing md:touch-none",
          hasDescription ? "items-start" : "items-center",
          isActive
            ? "bg-primary/10"
            : "hover:bg-accent",
          isDragging && "opacity-50 shadow-lg bg-background"
        )}
        onClick={onOpen}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onOpen(); }}
        {...attributes}
        {...listeners}
      >
        <div data-region="avatar" className="h-8 w-8 rounded bg-muted flex items-center justify-center overflow-hidden">
          {entry.hasImage ? (
            <img
              src={getCodexImageUrl(entry.id)}
              alt=""
              className="h-full w-full object-cover"
            />
          ) : (
            <Icon className="h-4 w-4 text-muted-foreground" />
          )}
        </div>
        <Tooltip open={dropdownOpen ? false : undefined}>
          <TooltipTrigger asChild>
            <div className="min-w-0 overflow-hidden">
              <div className="text-sm font-medium truncate">{entry.name}</div>
              {hasDescription && (
                <div className="text-xs text-muted-foreground truncate">
                  {entry.content}
                </div>
              )}
            </div>
          </TooltipTrigger>
          <TooltipContent 
            side="right" 
            sideOffset={24}
            className="codex-radix-tooltip max-w-xs p-2 bg-popover text-popover-foreground border border-border"
          >
            <CodexTooltipContent entry={entry} />
          </TooltipContent>
        </Tooltip>
        <DropdownMenu open={dropdownOpen} onOpenChange={setDropdownOpen}>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6 opacity-0 group-hover:opacity-100"
              onClick={(e) => e.stopPropagation()}
              onPointerDown={(e) => e.stopPropagation()}
            >
              <MoreHorizontal className="h-3 w-3" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={onOpen}>
              <Edit className="mr-2 h-4 w-4" />
              Open
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={onDelete}
              className="text-destructive focus:text-destructive"
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    );
  }
);

// Sortable wrapper for drag-and-drop
interface SortableCodexEntryProps {
  entry: CodexEntryType;
  onOpen: () => void;
  onDelete: () => void;
  isActive?: boolean;
}

export function SortableCodexEntry({ entry, onOpen, onDelete, isActive }: SortableCodexEntryProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: entry.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <CodexEntry
      ref={setNodeRef}
      entry={entry}
      onOpen={onOpen}
      onDelete={onDelete}
      isActive={isActive}
      isDragging={isDragging}
      style={style}
      attributes={attributes}
      listeners={listeners}
    />
  );
}

// Overlay component for drag preview
interface CodexEntryOverlayProps {
  entry: CodexEntryType;
}

export function CodexEntryOverlay({ entry }: CodexEntryOverlayProps) {
  usePortraitChanges();
  const Icon = TYPE_ICONS[entry.type] || User;
  const hasDescription = !!entry.content;

  return (
    <div 
      className={cn(
        "grid grid-cols-[32px_1fr_24px] gap-2 py-2 px-2 rounded bg-background border shadow-lg cursor-grabbing",
        hasDescription ? "items-start" : "items-center"
      )}
    >
      <div data-region="avatar" className="h-8 w-8 rounded bg-muted flex items-center justify-center overflow-hidden">
        {entry.hasImage ? (
          <img
            src={getCodexImageUrl(entry.id)}
            alt=""
            className="h-full w-full object-cover"
          />
        ) : (
          <Icon className="h-4 w-4 text-muted-foreground" />
        )}
      </div>
      <div className="min-w-0 overflow-hidden">
        <div className="text-sm font-medium truncate">{entry.name}</div>
        {hasDescription && (
          <div className="text-xs text-muted-foreground truncate">
            {entry.content}
          </div>
        )}
      </div>
      <div className="h-6 w-6" />
    </div>
  );
}
